/**
 * Microsoft Authentication Library (MSAL) Configuration
 * 
 * This module configures MSAL for Microsoft Entra ID (Azure AD) authentication.
 * Users authenticate with their Coveo Microsoft credentials.
 */
import { Configuration, LogLevel, PublicClientApplication } from '@azure/msal-browser';

const LOCAL_AUTH_ENVIRONMENTS = new Set(['development', 'local']);

export const isLocalAuthBypassEnabled = (): boolean => {
  const appEnv = (import.meta.env.VITE_APP_ENV || (import.meta.env.DEV ? 'development' : 'production')).toLowerCase();
  return LOCAL_AUTH_ENVIRONMENTS.has(appEnv) && import.meta.env.VITE_LOCAL_AUTH_BYPASS === 'true';
};

const isDevModeEnabled = (): boolean => isLocalAuthBypassEnabled();

// Microsoft Entra ID configuration from environment variables
const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_MICROSOFT_TENANT_ID || 'common'}`,
    redirectUri: import.meta.env.VITE_MICROSOFT_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: import.meta.env.VITE_MICROSOFT_POST_LOGOUT_URI || `${window.location.origin}/login`,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            // Only log in development
            if (import.meta.env.DEV) {
              console.info(message);
            }
            break;
          case LogLevel.Verbose:
            // Only log in development
            if (import.meta.env.DEV) {
              console.debug(message);
            }
            break;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Warning,
    },
  },
};

// Scopes to request from Microsoft
export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

// Check if Microsoft SSO is configured
export const isMicrosoftConfigured = (): boolean => {
  if (isDevModeEnabled()) {
    return false;
  }

  return !!(
    import.meta.env.VITE_MICROSOFT_CLIENT_ID &&
    import.meta.env.VITE_MICROSOFT_TENANT_ID
  );
};

// Create MSAL instance
let msalInstance: PublicClientApplication | null = null;

export const getMsalInstance = (): PublicClientApplication | null => {
  if (!isMicrosoftConfigured()) {
    console.warn('Microsoft SSO not configured. Set VITE_MICROSOFT_CLIENT_ID and VITE_MICROSOFT_TENANT_ID.');
    return null;
  }

  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }

  return msalInstance;
};

export { msalConfig };
export default getMsalInstance;
